import type { AuthEvent } from './events';

export interface CommunityStateSnapshot {
  communityId: string;
  ownerPubkey: string;
  mods: Set<string>;
  members: Set<string>;
  bannedPubkeys: Set<string>;
  channels: Array<{ id: string; name: string; createdAt: number }>;
  locked: boolean;
  invitedPubkeys: Set<string>;
}

export function isModOrOwner(pubkey: string, state: CommunityStateSnapshot): boolean {
  return pubkey === state.ownerPubkey || state.mods.has(pubkey);
}

export function deriveCommunityState(
  communityId: string,
  events: AuthEvent[]
): CommunityStateSnapshot | null {
  const relevant = events
    .filter(e => {
      const p = e.payload as any;
      return p.communityId === communityId || (e.type === 'community_create' && p.id === communityId);
    })
    .sort((a, b) => a.timestamp - b.timestamp);

  let state: CommunityStateSnapshot | null = null;

  for (const event of relevant) {
    const p = event.payload as any;
    const pubkey = event.pubkey;

    switch (event.type) {
      case 'community_create':
        if (p.id === communityId) {
          state = {
            communityId,
            ownerPubkey: pubkey,
            mods: new Set(),
            members: new Set([pubkey]),
            bannedPubkeys: new Set(),
            channels: [{ id: 'general', name: 'general', createdAt: event.timestamp }],
            locked: false,
            invitedPubkeys: new Set(),
          };
        }
        break;

      case 'community_join':
        if (!state) break;
        if (state.locked && !state.invitedPubkeys.has(pubkey)) break;
        if (!state.bannedPubkeys.has(pubkey)) state.members.add(pubkey);
        break;

      case 'community_leave':
        if (state) state.members.delete(pubkey);
        break;

      case 'community_lock':
        if (state && isModOrOwner(pubkey, state)) state.locked = true;
        break;

      case 'community_unlock':
        if (state && isModOrOwner(pubkey, state)) state.locked = false;
        break;

      case 'channel_create':
        if (state && isModOrOwner(pubkey, state) && p.channelId && p.channelName) {
          if (!state.channels.find(c => c.id === p.channelId)) {
            state.channels.push({ id: p.channelId, name: p.channelName, createdAt: event.timestamp });
          }
        }
        break;

      case 'channel_delete':
        if (state && isModOrOwner(pubkey, state) && p.channelId !== 'general') {
          state.channels = state.channels.filter(c => c.id !== p.channelId);
        }
        break;

      case 'mod_grant':
        if (state && pubkey === state.ownerPubkey && p.targetPubkey) state.mods.add(p.targetPubkey);
        break;

      case 'mod_revoke':
        if (state && pubkey === state.ownerPubkey && p.targetPubkey) state.mods.delete(p.targetPubkey);
        break;

      case 'community_ban':
        if (state && isModOrOwner(pubkey, state) && p.targetPubkey) {
          state.bannedPubkeys.add(p.targetPubkey);
          state.members.delete(p.targetPubkey);
          state.mods.delete(p.targetPubkey);
        }
        break;

      case 'community_unban':
        if (state && isModOrOwner(pubkey, state) && p.targetPubkey) {
          state.bannedPubkeys.delete(p.targetPubkey);
        }
        break;

      case 'community_invite':
        if (state && isModOrOwner(pubkey, state) && p.targetPubkey) {
          state.invitedPubkeys.add(p.targetPubkey);
        }
        break;
    }
  }

  return state;
}

export function canPublishEvent(
  pubkey: string,
  eventType: string,
  state: CommunityStateSnapshot
): boolean {
  if (state.bannedPubkeys.has(pubkey)) return false;

  const ownerOnly = ['mod_grant', 'mod_revoke'];
  const modOrOwner = ['community_lock', 'community_unlock', 'channel_create', 'channel_delete', 'community_ban', 'community_unban', 'community_pin_post', 'community_invite'];
  const memberOnly = ['channel_message', 'community_post', 'community_leave'];

  if (ownerOnly.includes(eventType)) return pubkey === state.ownerPubkey;
  if (modOrOwner.includes(eventType)) return isModOrOwner(pubkey, state);
  if (memberOnly.includes(eventType)) return state.members.has(pubkey);
  if (eventType === 'community_join') {
    if (state.locked) return state.invitedPubkeys.has(pubkey);
    return !state.bannedPubkeys.has(pubkey);
  }
  return false;
}
